import React, { useState, useEffect } from 'react';
import { 
  Button, Space, Row, Col, Tabs, Badge, Popover,
  Typography, message
} from 'antd';
import { 
  TeamOutlined, ShopOutlined,
  PlusOutlined, CheckCircleOutlined, InboxOutlined, CarOutlined, BellOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useProducts } from './hook/useProucts';
import { useAdaugaProduse } from "./hook/useAdaugaProduse"
import { useFurnizorPage } from './FurnizorPage/hook/useFurnizorPage';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';

// Importăm componentele
import AdaugaProduse from './tabs/AdaugaProduse.jsx';
import DecideFurnizori from './tabs/DecideFurnizori.jsx';
import SolicitariAchizitie from './tabs/SolicitariAchizitie.jsx';
import OferteLogistica from './tabs/OferteLogistica.jsx';
import Livrari from './tabs/Livrari.jsx';

// Importăm modalele
import ConfirmModal from './modals/ConfirmModal.jsx';
import FilterModal from './modals/FilterModal.jsx';
import HistoryModal from './modals/HistoryModal.jsx';
import DetailsModal from './modals/DetailsModal.jsx';

const { Title, Text } = Typography;

function Logistica() {
  const { 
    products, 
    loading,
    filters,
    applyFilters,
    fetchProductsInPurchaseStatuses,
    productToPurchaseRequests,
    productsInPurchaseStatuses,
    fetchOrganizedData
  } = useProducts();

  const {
    newProducts,
    loading: newProductsLoading,
    handleConfirmProduct,
    handleShowDetails,
    handleSearch,
    isFilterModalVisible,
    setIsFilterModalVisible,
    fetchNewProducts
  } = useAdaugaProduse();

  const { 
    getProductRequestStatuses,
    productRequestStatuses
  } = useFurnizorPage();

  const navigate = useNavigate();
  const { tab } = useParams();

  const [selectedNewProducts, setSelectedNewProducts] = useState([]);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedTabs, setLoadedTabs] = useState(new Set());



  useEffect(() => {
    // Simulare notificări noi
    const newNotification = {
      id: Date.now(),
      type: 'new_product',
      message: 'Produs nou adăugat: ' + (newProducts[0]?.product_name || ''),
      time: new Date()
    };
    if (newProducts.length > 0) {
      setNotifications(prev => [newNotification, ...prev]);
    }
  }, [newProducts]);

  // Mapare între parametrul URL și key-ul tab-ului
  const tabKeyMap = {
    'adauga-produse': '2',
    'decide-furnizori': '3', 
    'solicitari-achizitie': '4',
    'livrari': '5'
  };

  // Mapare inversă pentru navigare
  const keyTabMap = {
    '1': 'oferte-logistica',
    '2': 'adauga-produse',
    '3': 'decide-furnizori',
    '4': 'solicitari-achizitie', 
    '5': 'livrari'
  };

  // Determină tab-ul activ bazat pe parametrul URL
  const activeTab = tab ? tabKeyMap[tab] || '1' : '1';
  

  // Funcție pentru refresh datele tabului curent
  const refreshCurrentTabData = async () => {
    setRefreshing(true);
    try {
      switch (activeTab) {
        case '1':
          // Tab Oferte Logistica
          // TODO: Add fetch logic for Oferte Logistica if needed
          break;
        case '2':
          // Tab Adaugă Produse
          if (fetchNewProducts) {
            await fetchNewProducts();
          }
          break;
        case '3':
          // Tab Decide Furnizori - încarcă datele organizate
          console.log('🏭 Fetching DecideFurnizori organized data...');
          if (fetchOrganizedData) {
            await fetchOrganizedData();
            console.log('✅ DecideFurnizori data fetched successfully');
          } else {
            console.warn('⚠️ fetchOrganizedData function not available');
          }
          break;
        case '4':
          // Tab Solicitări de achiziție - se gestionează intern
          console.log('🛒 Solicitări de achiziție - managed internally by component');
          break;
        default:
          break;
      }
      message.success('Datele au fost actualizate cu succes!');
    } catch (error) {
      console.error('Eroare la refresh:', error);
      message.error('Eroare la actualizarea datelor');
    } finally {
      setRefreshing(false);
    }
  };

  // Funcție pentru fetch datele specifice unui tab
  const fetchTabData = async (tabKey) => {
    console.log(`🔄 Fetching data for tab: ${tabKey}`);
    try {
      switch (tabKey) {
        case '1':
          // Tab Oferte Logistica - încarcă datele pentru oferte logistica
          console.log('🚛 Fetching logistics offers...');
          // TODO: Add fetch logic for Oferte Logistica if needed
          break;
        case '2':
          // Tab Adaugă Produse - încarcă produsele noi
          console.log('📦 Fetching new products...');
          if (fetchNewProducts && !loadedTabs.has('2')) {
            await fetchNewProducts();
            console.log('✅ New products fetched successfully');
            setLoadedTabs(prev => new Set([...prev, '2']));
          } else {
            console.log('📦 New products already loaded or function not available');
          }
          break;
        case '3':
          // Tab Decide Furnizori - datele se gestionează automat prin hook-ul useProducts
          console.log('🏭 Tab Decide Furnizori - data is managed automatically by useProducts hook');
          // Componenta DecideFurnizori își gestionează singură datele prin hook-ul useProducts
          // Nu este nevoie de fetch manual suplimentar
          break;
        case '4':
          // Tab Solicitări de achiziție - NU face fetch automat
          console.log('🛒 Tab Solicitări de achiziție - fetch will be triggered manually');
          // Nu facem fetch automat - se va face manual când utilizatorul apasă butonul
          break;
        default:
          console.log(`❓ Unknown tab: ${tabKey}`);
          break;
      }
    } catch (error) {
      console.error(`❌ Error fetching data for tab ${tabKey}:`, error);
    }
  };

  // Effect pentru încărcarea datelor când se schimbă tab-ul
  useEffect(() => {
    console.log(`🔄 Tab changed to: ${activeTab}`);
    fetchTabData(activeTab);
  }, [activeTab, fetchNewProducts, fetchOrganizedData]); // Dependencies curate

  // Handler pentru schimbarea tab-ului
  const handleTabChange = (key) => {
    const tabSlug = keyTabMap[key];
    if (tabSlug) {
      navigate(`/dashboard/logistica/${tabSlug}`);
    } else {
      navigate('/dashboard/logistica');
    }
  };

  const handleFilterApply = () => {
    const newFilters = {
      ...filters,
      status: statusFilter,
      client: clientFilter,
      dateRange: dateRange
    };
    applyFilters(newFilters);
    setIsFilterModalVisible(false);
  };

  // Tabs moderne și intuitive
  const tabItems = [
    {
      key: "1",
      label: <><CarOutlined /> Oferte Logistica</>,
      children: (
        <OferteLogistica />
      ),
    },
    {
      key: '2',
      label: <><PlusOutlined /> Adaugă Produse</>,
      children: (
        <AdaugaProduse />
      ),
    },
    {
      key: '3',
      label: <><TeamOutlined /> Decide Furnizorii</>,
      children: (
        <DecideFurnizori />
      ),
    },
    {
      key: '4',
      label: <><InboxOutlined /> Solicitari de achizitie</>,
      children: (
        <SolicitariAchizitie />
      ),
    },
    {
      key: '5',
      label: <><InboxOutlined /> Livrari</>,
      children: (
        <Livrari />
      ),
    }
  ];

  return (
    <div className="p-4">
      
      <div className="mb-4">
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4}>Gestionare Produse</Title>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<ReloadOutlined />}
                loading={refreshing}
                onClick={refreshCurrentTabData}
                title="Actualizează datele tabului curent"
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Tabs 
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems} 
      />
      
      {/* Modale */}
      <ConfirmModal
        isVisible={isConfirmModalVisible}
        onClose={() => {
          setIsConfirmModalVisible(false);
          setSelectedNewProducts([]);
        }}
        selectedProducts={selectedNewProducts}
        onConfirm={() => {
          setIsConfirmModalVisible(false);
          setSelectedNewProducts([]);
        }}
      />

      <FilterModal
        isVisible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={handleFilterApply}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        dateRange={dateRange}
        setDateRange={setDateRange}
        products={products}
      />

      <HistoryModal
        isVisible={isHistoryModalVisible}
        onClose={() => setIsHistoryModalVisible(false)}
        selectedProduct={selectedProduct}
      />

      <DetailsModal
        isVisible={isDetailsModalVisible}
        onClose={() => setIsDetailsModalVisible(false)}
        selectedProduct={selectedProduct}
      />
    </div>
  );
}

export default Logistica;